import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  createQCLog,
  deleteQCLog,
  patchLot,
  patchQCLog,
  topUpLot,
} from "@/features/lots/api/lots.api"
import type {
  CreateQCLogPayload,
  PatchLotPayload,
  PatchQCLogPayload,
  TopUpLotPayload,
} from "@/features/lots/api/types"
import { lotKeys } from "@/features/lots/hooks/use-lots"
import { deleteDocument } from "@/features/documents/api/documents.api"
import { extractApiErrorMessage } from "@/lib/api/client"

function useLotInvalidator() {
  const queryClient = useQueryClient()
  return (lotId?: string | number) => {
    queryClient.invalidateQueries({ queryKey: lotKeys.all })
    if (lotId) {
      queryClient.invalidateQueries({ queryKey: lotKeys.details(lotId) })
    }
  }
}

export function usePatchLot(lotId: string | number) {
  const { t } = useTranslation("lots")
  const invalidate = useLotInvalidator()
  return useMutation({
    mutationFn: (payload: PatchLotPayload) => patchLot(lotId, payload),
    onSuccess: (_data, variables) => {
      if (variables.status === "COMPLETED") {
        toast.success(t("toast.completed"))
      } else {
        toast.success(t("toast.updated"))
      }
      invalidate(lotId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useTopUpLot(lotId: string | number) {
  const { t } = useTranslation("lots")
  const invalidate = useLotInvalidator()
  return useMutation({
    mutationFn: (payload: TopUpLotPayload) => topUpLot(lotId, payload),
    onSuccess: () => {
      toast.success(t("toast.toppedUp"))
      invalidate(lotId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useCreateQCLog(lotId: string | number) {
  const { t } = useTranslation("lots")
  const invalidate = useLotInvalidator()
  return useMutation({
    mutationFn: (payload: CreateQCLogPayload) => createQCLog(lotId, payload),
    onSuccess: () => {
      toast.success(t("toast.qcAdded"))
      invalidate(lotId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function usePatchQCLog(lotId: string | number) {
  const invalidate = useLotInvalidator()
  return useMutation({
    mutationFn: ({
      logId,
      payload,
    }: {
      logId: string | number
      payload: PatchQCLogPayload
    }) => patchQCLog(lotId, logId, payload),
    onSuccess: () => {
      invalidate(lotId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

// Delete a document attached to a QC log via the shared documents
// service. The service's `level=lotQc` branch will both remove the R2
// object and prune the corresponding entry from the QC log's `documents`
// JSONB array — we don't have a dedicated lots route for this.
export function useDeleteQCLogDoc(lotId: string | number) {
  const { t } = useTranslation("lots")
  const invalidate = useLotInvalidator()
  return useMutation({
    mutationFn: ({
      logId,
      storedKey,
    }: {
      logId: string | number
      storedKey: string
    }) =>
      deleteDocument({
        level: "lotQc",
        documentKey: storedKey,
        lotId: String(lotId),
        logId: String(logId),
      }),
    onSuccess: () => {
      toast.success(t("toast.docDeleted"))
      invalidate(lotId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useDeleteQCLog(lotId: string | number) {
  const { t } = useTranslation("lots")
  const invalidate = useLotInvalidator()
  return useMutation({
    mutationFn: (logId: string | number) => deleteQCLog(lotId, logId),
    onSuccess: () => {
      toast.success(t("toast.qcRemoved"))
      invalidate(lotId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}
